// src/plugins/sales-forecast/components/InterestRateControl.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Slider,
  SliderTrack,
  SliderRange,
  SliderThumb,
} from "../../../components/ui/slider";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

export const InterestRateControl: React.FC<{
  initialRate?: number;
  initialThreeMonthChange?: number;
  initialSixMonthChange?: number;
  initialNineMonthChange?: number;
  initialTwelveMonthChange?: number;
  initialTwoYearChange?: number;
  onChange: (rate: number) => void;
  onChangeThreeMonthChange?: (delta: number) => void;
  onChangeSixMonthChange?: (delta: number) => void;
  onChangeNineMonthChange?: (delta: number) => void;
  onChangeTwelveMonthChange?: (delta: number) => void;
  onChangeTwoYearChange?: (delta: number) => void;
  min?: number;
  max?: number;
  step?: number;
}> = ({
  initialRate = 4,
  initialThreeMonthChange = 0,
  initialSixMonthChange = 0,
  initialNineMonthChange = 0,
  initialTwelveMonthChange = 0,
  initialTwoYearChange = 0,
  onChange,
  onChangeThreeMonthChange,
  onChangeSixMonthChange,
  onChangeNineMonthChange,
  onChangeTwelveMonthChange,
  onChangeTwoYearChange,
  min = 0,
  max = 10,
  step = 0.1,
}) => {
  const theme = useSystemTheme();

  const [rate, setRate] = useState(initialRate);
  const [threeMonthChange, setThreeMonthChange] = useState(initialThreeMonthChange);
  const [sixMonthChange, setSixMonthChange] = useState(initialSixMonthChange);
  const [nineMonthChange, setNineMonthChange] = useState(initialNineMonthChange);
  const [twelveMonthChange, setTwelveMonthChange] = useState(initialTwelveMonthChange);
  const [twoYearChange, setTwoYearChange] = useState(initialTwoYearChange);

  // --- Sync props from parent when they change ---
  useEffect(() => {
    setRate(initialRate);
    setThreeMonthChange(initialThreeMonthChange);
    setSixMonthChange(initialSixMonthChange);
    setNineMonthChange(initialNineMonthChange);
    setTwelveMonthChange(initialTwelveMonthChange);
    setTwoYearChange(initialTwoYearChange);
  }, [
    initialRate,
    initialThreeMonthChange,
    initialSixMonthChange,
    initialNineMonthChange,
    initialTwelveMonthChange,
    initialTwoYearChange,
  ]);

  const [rateError, setRateError] = useState("");
  const [threeMonthChangeError, setThreeMonthChangeError] = useState("");
  const [sixMonthChangeError, setSixMonthChangeError] = useState("");
  const [nineMonthChangeError, setNineMonthChangeError] = useState("");
  const [twelveMonthChangeError, setTwelveMonthChangeError] = useState("");
  const [twoYearChangeError, setTwoYearChangeError] = useState("");

  const isFirstRun = useRef(true);

  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const cardBg = css.getPropertyValue("--subcard-bg").trim(); // lighter background for card & sliders
  const inputBg = css.getPropertyValue("--card-bg").trim(); // darker color for inputs
  const mutedText = css.getPropertyValue("--muted-text-color").trim();

  // --- Validation and updates ---
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }

    const validate = (val: number) => val >= min && val <= max;

    setRateError(validate(rate) ? "" : `Rate must be between ${min}% and ${max}%`);
    setThreeMonthChangeError(
      validate(threeMonthChange)
        ? ""
        : `3-Month change must be between ${min}% and ${max}%`
    );
    setSixMonthChangeError(
      validate(sixMonthChange)
        ? ""
        : `6-Month change must be between ${min}% and ${max}%`
    );
    setNineMonthChangeError(
      validate(nineMonthChange)
        ? ""
        : `9-Month change must be between ${min}% and ${max}%`
    );
    setTwelveMonthChangeError(
      validate(twelveMonthChange)
        ? ""
        : `12-Month change must be between ${min}% and ${max}%`
    );
    setTwoYearChangeError(
      validate(twoYearChange)
        ? ""
        : `24-Month change must be between ${min}% and ${max}%`
    );

    if (validate(rate)) onChange(rate);
    if (validate(threeMonthChange) && onChangeThreeMonthChange)
      onChangeThreeMonthChange(threeMonthChange);
    if (validate(sixMonthChange) && onChangeSixMonthChange)
      onChangeSixMonthChange(sixMonthChange);
    if (validate(nineMonthChange) && onChangeNineMonthChange)
      onChangeNineMonthChange(nineMonthChange);
    if (validate(twelveMonthChange) && onChangeTwelveMonthChange)
      onChangeTwelveMonthChange(twelveMonthChange);
    if (validate(twoYearChange) && onChangeTwoYearChange)
      onChangeTwoYearChange(twoYearChange);
  }, [
    rate,
    threeMonthChange,
    sixMonthChange,
    nineMonthChange,
    twelveMonthChange,
    twoYearChange,
    min,
    max,
    onChange,
    onChangeThreeMonthChange,
    onChangeSixMonthChange,
    onChangeNineMonthChange,
    onChangeTwelveMonthChange,
    onChangeTwoYearChange,
  ]);

  const handleInput =
    (setter: React.Dispatch<React.SetStateAction<number>>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseFloat(e.target.value);
      setter(!isNaN(val) ? val : (NaN as unknown as number));
    };

  const handleSlider =
    (setter: React.Dispatch<React.SetStateAction<number>>) =>
    (vals: number[]) =>
      setter(vals[0]);

  const renderSection = (
    id: string,
    label: string,
    value: number,
    onInput: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onSlide: (vals: number[]) => void,
    error: string
  ) => (
    <div className="space-y-3">
      <Label htmlFor={id} className="font-semibold" style={{ color: textColor }}>
        {label}
      </Label>

      {/* Slider area - stays light */}
      <div
        className="p-1 rounded-md"
        style={{
          backgroundColor: cardBg,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Slider
          id={id}
          value={[Number.isFinite(value) ? value : 0]}
          min={min}
          max={max}
          step={step}
          onValueChange={onSlide}
        >
          <SliderTrack>
            <SliderRange />
          </SliderTrack>
          <SliderThumb />
        </Slider>
      </div>

      {/* Input area - dark */}
      <div className="flex items-center space-x-2">
        <Input
          id={`${id}-input`}
          type="number"
          step={step}
          value={Number.isFinite(value) ? value : ""}
          onChange={onInput}
          className="w-24"
          style={{
            backgroundColor: inputBg,
            color: textColor,
            border: `1px solid ${borderColor}`,
            borderRadius: "6px",
            padding: "0.3rem 0.4rem",
          }}
        />
        <span style={{ color: mutedText }}>%</span>
      </div>

      {/* Error alert */}
      {error && (
        <Alert
          variant="destructive"
          className="mt-2"
          style={{
            backgroundColor: "var(--error-bg)",
            color: "var(--error-text)",
            borderColor: "var(--error-border)",
          }}
        >
          {error}
        </Alert>
      )}
    </div>
  );

  return (
    <Card
      className={`interest-rate-control theme-${theme}`}
      style={{
        backgroundColor: cardBg,
        color: textColor,
        border: `1px solid ${borderColor}`,
        borderRadius: "12px",
        boxShadow: "0 1px 6px rgba(0,0,0,0.25)",
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
    >
      <CardHeader>
        <CardTitle style={{ color: textColor, fontWeight: 700 }}>
          Interest Rate
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {renderSection("rate", "Rate (%)", rate, handleInput(setRate), handleSlider(setRate), rateError)}
        {renderSection(
          "threeMonth",
          "3-Month Interest Rate (%)",
          threeMonthChange,
          handleInput(setThreeMonthChange),
          handleSlider(setThreeMonthChange),
          threeMonthChangeError
        )}
        {renderSection(
          "sixMonth",
          "6-Month Interest Rate (%)",
          sixMonthChange,
          handleInput(setSixMonthChange),
          handleSlider(setSixMonthChange),
          sixMonthChangeError
        )}
        {renderSection(
          "nineMonth",
          "9-Month Interest Rate (%)",
          nineMonthChange,
          handleInput(setNineMonthChange),
          handleSlider(setNineMonthChange),
          nineMonthChangeError
        )}
        {renderSection(
          "twelveMonth",
          "12-Month Interest Rate (%)",
          twelveMonthChange,
          handleInput(setTwelveMonthChange),
          handleSlider(setTwelveMonthChange),
          twelveMonthChangeError
        )}
        {renderSection(
          "twoYear",
          "24-Month Interest Rate (%)",
          twoYearChange,
          handleInput(setTwoYearChange),
          handleSlider(setTwoYearChange),
          twoYearChangeError
        )}
      </CardContent>
    </Card>
  );
};